import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { adminService } from '../services/adminService';

export default function Collaborations() {
  const [collaborations, setCollaborations] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [fee, setFee] = useState(0);
  const [newFee, setNewFee] = useState('');
  const [activeTab, setActiveTab] = useState('collaborations');
  const [loading, setLoading] = useState(false);
  // Search & Filter states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [collabs, requests, feeData] = await Promise.all([
      adminService.getCollaborations(),
      adminService.getContactRequests(),
      adminService.getContactRequestFee(),
    ]);
    setCollaborations(collabs);
    setContactRequests(requests);
    setFee(feeData.fee);
    setLoading(false);
  };

  // Filtered data for Collaborations
  const filteredCollaborations = collaborations.filter(col => {
    // Text search (name/email/role)
    const searchText = search.toLowerCase();
    const userMatch = col.user && (
      col.user.name?.toLowerCase().includes(searchText) ||
      col.user.email?.toLowerCase().includes(searchText) ||
      col.user.role?.toLowerCase().includes(searchText)
    );
    const collabMatch = col.collaborator && (
      col.collaborator.name?.toLowerCase().includes(searchText) ||
      col.collaborator.email?.toLowerCase().includes(searchText) ||
      col.collaborator.role?.toLowerCase().includes(searchText)
    );
    // Date filter
    let dateOk = true;
    if (dateFrom) {
      dateOk = dateOk && new Date(col.created_at) >= dateFrom;
    }
    if (dateTo) {
      dateOk = dateOk && new Date(col.created_at) <= dateTo;
    }
    return (
      (!search || userMatch || collabMatch) &&
      dateOk
    );
  });

  // Filtered data for Contact Requests
  const filteredRequests = contactRequests.filter(req => {
    const searchText = search.toLowerCase();
    const senderMatch = req.sender && (
      req.sender.name?.toLowerCase().includes(searchText) ||
      req.sender.email?.toLowerCase().includes(searchText) ||
      req.sender.role?.toLowerCase().includes(searchText)
    );
    const receiverMatch = req.receiver && (
      req.receiver.name?.toLowerCase().includes(searchText) ||
      req.receiver.email?.toLowerCase().includes(searchText) ||
      req.receiver.role?.toLowerCase().includes(searchText)
    );
    // Status filter
    const statusOk = !statusFilter || req.status === statusFilter;
    // Date filter
    let dateOk = true;
    if (dateFrom) {
      dateOk = dateOk && new Date(req.created_at) >= dateFrom;
    }
    if (dateTo) {
      dateOk = dateOk && new Date(req.created_at) <= dateTo;
    }
    return (
      (!search || senderMatch || receiverMatch) &&
      statusOk &&
      dateOk
    );
  });

  const handleFeeUpdate = async () => {
    await adminService.updateContactRequestFee(newFee);
    setFee(newFee);
    setNewFee('');
    alert('Fee updated!');
  };

  const statusBadge = status => {
    let color = 'gray';
    if (status === 'pending') color = 'yellow';
    if (status === 'accepted') color = 'green';
    if (status === 'rejected') color = 'red';
    return (
      <span className={`px-2 py-1 rounded text-xs bg-${color}-100 text-${color}-800 font-semibold`}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Page Title & Breadcrumb */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-1">Collaborations & Contact Requests</h1>
          <div className="text-sm text-gray-500">Manage all collaborations and contact requests here.</div>
        </div>
        <button
          className="btn-secondary"
          onClick={fetchData}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {/* Contact Request Fee Card */}
      <div className="card p-6 mb-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-2">Contact Request Fee</h3>
          <span>
            Current Fee: <b>{fee}</b> coins
          </span>
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            value={newFee}
            onChange={e => setNewFee(e.target.value)}
            placeholder="Set new fee"
            className="input-field w-32"
          />
          <button
            onClick={handleFeeUpdate}
            className="btn-primary"
            disabled={!newFee}
          >
            Update
          </button>
        </div>
      </div>

      {/* Tabs & Filters */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
        <div className="flex gap-2">
          <button
            className={`btn-secondary ${activeTab === 'collaborations' ? 'bg-primary-600 text-white' : ''}`}
            onClick={() => setActiveTab('collaborations')}
          >
            Collaborations
          </button>
          <button
            className={`btn-secondary ${activeTab === 'requests' ? 'bg-primary-600 text-white' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Contact Requests
          </button>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <input
            type="text"
            className="input-field w-48"
            placeholder="Search name, email, role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {activeTab === 'requests' && (
            <select
              className="input-field"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          )}
          <DatePicker
            selected={dateFrom}
            onChange={setDateFrom}
            selectsStart
            startDate={dateFrom}
            endDate={dateTo}
            placeholderText="From date"
            className="input-field w-32"
            isClearable
          />
          <DatePicker
            selected={dateTo}
            onChange={setDateTo}
            selectsEnd
            startDate={dateFrom}
            endDate={dateTo}
            minDate={dateFrom}
            placeholderText="To date"
            className="input-field w-32"
            isClearable
          />
          {(dateFrom || dateTo || search || statusFilter) && (
            <button className="btn-secondary" onClick={() => {
              setSearch(''); setStatusFilter(''); setDateFrom(null); setDateTo(null);
            }}>Clear</button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="card p-0 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : activeTab === 'collaborations' ? (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">User 1</th>
                <th className="px-4 py-2 text-left">User 2</th>
                <th className="px-4 py-2 text-left">Created At</th>
                <th className="px-4 py-2 text-left">Updated At</th>
              </tr>
            </thead>
            <tbody>
              {filteredCollaborations.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-gray-500">
                    No collaborations found.
                  </td>
                </tr>
              ) : filteredCollaborations.map(col => (
                <tr key={col.id} className="border-t">
                  {/* User 1 */}
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {col.user?.profilePicture ? (
                        <img src={col.user.profilePicture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">?</div>
                      )}
                      <div>
                        <div className="font-semibold">{col.user?.name || <span className="text-gray-400">Unknown</span>}</div>
                        <div className="text-xs text-gray-500">{col.user?.email || '-'}</div>
                        <div className="text-xs text-gray-400">{col.user?.role || '-'}</div>
                      </div>
                    </div>
                  </td>
                  {/* User 2 */}
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {col.collaborator?.profilePicture ? (
                        <img src={col.collaborator.profilePicture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">?</div>
                      )}
                      <div>
                        <div className="font-semibold">{col.collaborator?.name || <span className="text-gray-400">Unknown</span>}</div>
                        <div className="text-xs text-gray-500">{col.collaborator?.email || '-'}</div>
                        <div className="text-xs text-gray-400">{col.collaborator?.role || '-'}</div>
                      </div>
                    </div>
                  </td>
                  {/* Created At */}
                  <td className="px-4 py-2">{col.created_at ? new Date(col.created_at).toLocaleString() : '-'}</td>
                  {/* Updated At */}
                  <td className="px-4 py-2">{col.updated_at ? new Date(col.updated_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Sender</th>
                <th className="px-4 py-2 text-left">Receiver</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Created At</th>
                <th className="px-4 py-2 text-left">Updated At</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-gray-500">
                    No contact requests found.
                  </td>
                </tr>
              ) : filteredRequests.map(req => (
                <tr key={req.id} className="border-t">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      {req.sender?.profilePicture ? (
                        <img src={req.sender.profilePicture} alt="" className="w-8 h-8 rounded-full border" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">?</div>
                      )}
                      <div>
                        <div className="font-semibold">{req.sender?.name || <span className="text-gray-400">Unknown</span>}</div>
                        <div className="text-xs text-gray-500">{req.sender?.email || '-'}</div>
                        <div className="text-xs text-gray-400">{req.sender?.role || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      {req.receiver?.profilePicture ? (
                        <img src={req.receiver.profilePicture} alt="" className="w-8 h-8 rounded-full border" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">?</div>
                      )}
                      <div>
                        <div className="font-semibold">{req.receiver?.name || <span className="text-gray-400">Unknown</span>}</div>
                        <div className="text-xs text-gray-500">{req.receiver?.email || '-'}</div>
                        <div className="text-xs text-gray-400">{req.receiver?.role || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2">{statusBadge(req.status)}</td>
                  <td className="px-4 py-2">{req.created_at ? new Date(req.created_at).toLocaleString() : '-'}</td>
                  <td className="px-4 py-2">{req.updated_at ? new Date(req.updated_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}